import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertTriangle, RefreshCw, Shield, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface InvalidCertificate {
  certificate_id: string;
  user_id: string;
  course_id: string;
  certificate_number: string;
  issue_date: string;
  completed_modules: number;
  total_modules: number;
  reason: string;
}

/**
 * Phase 5: Admin Tool for Managing Invalid Certificates
 * Identifies and allows revocation of certificates issued prematurely
 */
const InvalidCertificates: React.FC = () => {
  const [invalidCerts, setInvalidCerts] = useState<InvalidCertificate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFixing, setIsFixing] = useState(false);
  const [fixedCount, setFixedCount] = useState(0);

  const loadInvalidCertificates = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.rpc('get_invalid_certificates');
      
      if (error) throw error;
      
      setInvalidCerts(data || []);
    } catch (error) {
      console.error('Error loading invalid certificates:', error);
      toast.error('Failed to load invalid certificates');
    } finally {
      setIsLoading(false);
    }
  };

  const fixCourseAssignmentProgress = async () => {
    try {
      setIsFixing(true);
      const { data, error } = await supabase.rpc('fix_course_assignment_progress');
      
      if (error) throw error;
      
      const fixed = data?.length || 0;
      setFixedCount(fixed);
      toast.success(`Fixed ${fixed} course assignment progress records`);
      
      // Reload invalid certificates
      await loadInvalidCertificates();
    } catch (error) {
      console.error('Error fixing progress:', error);
      toast.error('Failed to fix course assignment progress');
    } finally {
      setIsFixing(false);
    }
  };

  const revokeCertificate = async (certId: string, reason: string) => {
    try {
      const { error } = await supabase
        .from('certificates')
        .update({
          revoked: true,
          revoked_at: new Date().toISOString(),
          revoked_reason: reason,
          revoked_by: (await supabase.auth.getUser()).data.user?.id
        })
        .eq('id', certId);

      if (error) throw error;

      toast.success('Certificate revoked successfully');
      await loadInvalidCertificates();
    } catch (error) {
      console.error('Error revoking certificate:', error);
      toast.error('Failed to revoke certificate');
    }
  };

  const bulkRevoke = async () => {
    if (!confirm(`Revoke ${invalidCerts.length} invalid certificates? This action cannot be undone.`)) {
      return;
    }

    try {
      let revoked = 0;
      for (const cert of invalidCerts) {
        await revokeCertificate(cert.certificate_id, cert.reason);
        revoked++;
      }
      toast.success(`Successfully revoked ${revoked} invalid certificates`);
    } catch (error) {
      console.error('Error in bulk revocation:', error);
      toast.error('Bulk revocation encountered errors');
    }
  };

  useEffect(() => {
    loadInvalidCertificates();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Invalid Certificates</h1>
        <p className="text-muted-foreground mt-2">
          Phase 5: Identify and manage certificates issued prematurely
        </p>
      </div>

      {/* Stats Card */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Invalid Certificates</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{invalidCerts.length}</div>
            <p className="text-xs text-muted-foreground">
              Certificates issued with incomplete modules
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Fixed This Session</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{fixedCount}</div>
            <p className="text-xs text-muted-foreground">
              Progress records corrected
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Protection Status</CardTitle>
            <Shield className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">Active</div>
            <p className="text-xs text-muted-foreground">
              Database triggers preventing new invalid certificates
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Emergency Actions</CardTitle>
          <CardDescription>
            Fix data issues and manage invalid certificates
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <Button
              onClick={fixCourseAssignmentProgress}
              disabled={isFixing}
              variant="outline"
            >
              {isFixing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <RefreshCw className="mr-2 h-4 w-4" />
              Fix Progress Calculations
            </Button>

            <Button
              onClick={bulkRevoke}
              disabled={invalidCerts.length === 0}
              variant="destructive"
            >
              <AlertTriangle className="mr-2 h-4 w-4" />
              Bulk Revoke All ({invalidCerts.length})
            </Button>

            <Button
              onClick={loadInvalidCertificates}
              variant="ghost"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Before revoking:</strong> Run "Fix Progress Calculations" to ensure course_assignments 
              data is accurate. Some certificates may become valid after fixing progress data.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Invalid Certificates Table */}
      <Card>
        <CardHeader>
          <CardTitle>Invalid Certificates Found</CardTitle>
          <CardDescription>
            {invalidCerts.length === 0 
              ? '✅ No invalid certificates found. All certificates are valid!' 
              : `${invalidCerts.length} certificate(s) issued prematurely`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invalidCerts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-600" />
              <p className="text-lg font-medium">All Clear!</p>
              <p className="text-sm">No invalid certificates detected in the system.</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Certificate #</TableHead>
                    <TableHead>Course</TableHead>
                    <TableHead>Issue Date</TableHead>
                    <TableHead>Modules</TableHead>
                    <TableHead>Issue</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invalidCerts.map((cert) => (
                    <TableRow key={cert.certificate_id}>
                      <TableCell className="font-mono text-xs">
                        {cert.certificate_number.substring(0, 20)}...
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {cert.course_id}
                      </TableCell>
                      <TableCell>
                        {new Date(cert.issue_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant={cert.completed_modules < cert.total_modules ? 'destructive' : 'secondary'}>
                          {cert.completed_modules}/{cert.total_modules}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[250px]">
                        <p className="text-xs text-muted-foreground truncate">
                          {cert.reason}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => revokeCertificate(cert.certificate_id, cert.reason)}
                        >
                          Revoke
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InvalidCertificates;
